import React, { useRef, useState } from "react";
import Dropdown from "../Elements/Dropdown";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import Lottie from "lottie-react";
import { TbChevronDown } from "react-icons/tb"; // import the icon if not already
import PropTypes from "prop-types";

const CARD_MAX_HEIGHT_CLASS = "sm:max-h-84 md:max-h-88 lg:max-h-92 xl:max-h-96";

const Card = ({
  title,
  description,
  options,
  onClick,
  children,
  startButton,
  details,
  className,
  cardClassName,
  useMaxHeight = false,
  lottieAnimation,
  carousel = false,
}) => {
  const lottieRef = useRef(null);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Track dropdown open state

  const handleMouseEnter = () => {
    if (lottieRef.current && !isAnimationPlaying) {
      lottieRef.current.play();
      setIsAnimationPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    // No action needed
  };

  return (
    <div className="card-base card-base-background w-full h-full card-base-border overflow-hidden">
      <div
        className={`flex flex-col ${useMaxHeight && !cardClassName ? CARD_MAX_HEIGHT_CLASS : ""} ${cardClassName || "h-full"}`}
      >
        <div className="flex justify-between items-center p-3 border-bottom-base min-w-0">
          <div className="flex flex-row gap-2 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-xl truncate">{title}</h2>
              <p className="text-sm md:text-xs truncate">{description}</p>
            </div>
          </div>
          {options && (
            <div className="flex-shrink-0 ml-2">
              <Dropdown
                options={options}
                onOpenChange={setIsDropdownOpen}
                isOpen={isDropdownOpen}
                trigger={
                  <span
                    className={`flex items-center justify-center w-8 h-8 p-2 rounded-full transition duration-300 cursor-pointer ${
                      isDropdownOpen ? "bg-blue-100" : "hover:bg-blue-100"
                    }`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <TbChevronDown
                      size={17}
                      className={`transition-transform duration-300 ${
                        isDropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </span>
                }
              />
            </div>
          )}
        </div>

        <div
          className={`${carousel ? 'py-3' : 'p-3'} flex-1 relative flex overflow-hidden`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children && React.Children.count(children) > 0 ? (
            <div
              className={`flex flex-col gap-1 w-full h-full overflow-auto ${className || ""}`}
            >
              {carousel ? (
                <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar pl-3 pr-3">
                  {React.Children.map(children, (child, index) => {
                    const isFirst = index === 0;
                    const isLast = index === React.Children.count(children) - 1;
                    return React.cloneElement(child, {
                      className: `${child.props.className || ''} ${isFirst ? 'ml-0' : ''} ${isLast ? 'mr-0' : ''}`.trim()
                    });
                  })}
                </div>
              ) : (
                children
              )}
            </div>
          ) : (
            <div
              className={`flex flex-col gap-1 w-full h-full overflow-auto items-center justify-center ${className || ""}`}
            >
              {lottieAnimation && (
                <Lottie
                  animationData={lottieAnimation}
                  loop
                  autoplay
                  style={{ width: 150, height: 150 }}
                />
              )}
              {details && (
                <div className="mt-2 text-center text-s max-w-70">
                  {details}
                </div>
              )}
              {startButton && onClick && (
                <div className="mt-4">
                  <BtnSecondaryM
                    buttonCTA={startButton}
                    onClickLink={onClick}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Card.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  options: PropTypes.array,
  onClick: PropTypes.func,
  children: PropTypes.node,
  startButton: PropTypes.string,
  details: PropTypes.string,
  className: PropTypes.string,
  cardClassName: PropTypes.string,
  useMaxHeight: PropTypes.bool,
  lottieAnimation: PropTypes.object,
  carousel: PropTypes.bool,
};

export default Card;
